import { Component } from '@theme/component';

/**
 * @typedef {Object} DmProductCarouselRefs
 * @property {HTMLElement} track
 * @property {HTMLButtonElement} [prevBtn]
 * @property {HTMLButtonElement} [nextBtn]
 * @property {HTMLElement} [progressFill]
 */

/** @extends {Component<DmProductCarouselRefs>} */
class DmProductCarousel extends Component {
  /** @type {ResizeObserver|null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.track?.addEventListener('scroll', () => this.#update(), { passive: true });

    // ResizeObserver catches layout changes after images/fonts load — rAF alone fires too early
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    if (this.refs.track) this.#resizeObserver.observe(this.refs.track);

    requestAnimationFrame(() => this.#update());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  prev() {
    this.refs.track?.scrollBy({ left: -this.#cardStep(), behavior: 'smooth' });
  }

  next() {
    this.refs.track?.scrollBy({ left: this.#cardStep(), behavior: 'smooth' });
  }

  #cardStep() {
    const track = this.refs.track;
    const card = track ? /** @type {HTMLElement|null} */ (track.querySelector('.dm-product-recs__card')) : null;
    if (!card || !track) return 320;
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    return card.offsetWidth + gap;
  }

  #update() {
    const track = this.refs.track;
    if (!track) return;

    const { scrollLeft, scrollWidth, clientWidth } = track;
    const maxScroll = scrollWidth - clientWidth;
    // 2px tolerance — sub-pixel rounding can produce tiny positive maxScroll
    // even when all cards fit and no actual scrolling is possible
    const canScroll = maxScroll > 2;

    if (this.refs.prevBtn) this.refs.prevBtn.disabled = scrollLeft <= 0;
    if (this.refs.nextBtn) this.refs.nextBtn.disabled = !canScroll || scrollLeft >= maxScroll - 1;

    const fill = this.refs.progressFill;
    if (!fill) return;

    if (!canScroll) {
      fill.style.width = '100%';
      fill.style.left = '0';
    } else {
      // Fill width = 1 card slot (1/totalCards). Matches Figma: fill is a fixed-width
      // thumb that slides, not a proportional region of the visible viewport.
      const totalCards = track.querySelectorAll('.dm-product-recs__card').length;
      const fillPct = totalCards > 0 ? 100 / totalCards : 100;
      const scrollPct = scrollLeft / maxScroll; // 0 → 1
      fill.style.width = `${fillPct}%`;
      fill.style.left = `${scrollPct * (100 - fillPct)}%`;
    }
  }
}

if (!customElements.get('dm-product-carousel')) {
  customElements.define('dm-product-carousel', DmProductCarousel);
}
